// Tamil-Katral-Maiyam\src\pages\student\Profile.tsx

import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { showErrorToast, showSuccessToast } from "../../utils/errorHandler";
import { sanitizePhone, PHONE_MAX_CHARS } from "../../utils/validation";
import {
  getStudentMe,
  patchStudentMe,
  RosterStudent,
} from "../../modules/roster/api/roster.client";
import { ROSTER_STUDENT_ME_QUERY_KEY } from "../../modules/roster/queryKeys";
import { Spinner } from "../../components/ui/Spinner";
import { Badge } from "../../components/ui/Badge";
import { Pencil, Save, X } from "lucide-react";

interface StudentFormData {
  display_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  dob?: string;
  gender?: string;
  address?: string;
  guardian_name?: string;
}

function getInitial(name?: string) {
  const s = (name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "S";
}

export const StudentProfile: React.FC = () => {
  const queryClient = useQueryClient();
  const [student, setStudent] = useState<RosterStudent | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, setValue } = useForm<StudentFormData>({
    defaultValues: {
      display_name: "",
      first_name: "",
      last_name: "",
      phone: "",
      dob: "",
      gender: "",
      address: "",
      guardian_name: "",
    },
  });

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await getStudentMe();
      setStudent(data);
      reset({
        display_name: data.display_name || "",
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        phone: data.phone || "",
        dob: data.dob || "",
        gender: data.gender || "",
        address: data.address || "",
        guardian_name: data.guardian_name || "",
      });
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    if (student) {
      reset({
        display_name: student.display_name || "",
        first_name: student.first_name || "",
        last_name: student.last_name || "",
        phone: student.phone || "",
        dob: student.dob || "",
        gender: student.gender || "",
        address: student.address || "",
        guardian_name: student.guardian_name || "",
      });
    }
    setIsEditing(false);
  };

  const onSubmit = async (values: StudentFormData) => {
    if (!student) return;
    setIsSaving(true);
    try {
      await patchStudentMe(values, student.id);
      showSuccessToast("Profile updated successfully");
      await queryClient.invalidateQueries({ queryKey: ROSTER_STUDENT_ME_QUERY_KEY });
      await loadProfile();
      setIsEditing(false);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex w-full justify-center py-12">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full flex-1">
        <div className="mx-auto w-full max-w-5xl px-6 py-8 space-y-6">
          <PageHeader
            title="Your Profile"
            subtitle="View and edit your details"
            actions={
              isEditing ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isSaving}
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                </div>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )
            }
          />

          <Card
            className="w-full border border-slate-200 dark:border-slate-700 border-t-4 border-t-primary-500 rounded-2xl shadow-lg overflow-hidden"
            padding="none"
          >
            {/* Hero / Avatar section with soft gradient */}
            <div className="bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 px-6 py-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0 ring-4 ring-white/60 dark:ring-slate-800/60 shadow-md">
                  <span className="text-2xl font-bold text-primary-700 dark:text-primary-300">
                    {getInitial(student?.display_name)}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    {student?.display_name || "Student"}
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                    Tamil Learning Student
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge
                      variant={student?.status === "active" ? "success" : "gray"}
                    >
                      {student?.status || "-"}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Account info (read-only) */}
              {/* <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border-l-4 border-primary-400">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Account
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Student ID
                    </p>
                    <p className="text-sm text-slate-900 dark:text-white mt-0.5">
                      {student?.id || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Admission No
                    </p>
                    <p className="text-sm text-slate-900 dark:text-white mt-0.5">
                      {student?.admission_no || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Status
                    </p>
                    <div className="mt-0.5">
                      <Badge
                        variant={
                          student?.status === "active" ? "success" : "gray"
                        }
                      >
                        {student?.status || "-"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Tenant
                    </p>
                    <p className="text-sm text-slate-900 dark:text-white mt-0.5">
                      {student?.tenant_id || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Created
                    </p>
                    <p className="text-sm text-slate-900 dark:text-white mt-0.5">
                      {student?.created_at || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      Updated
                    </p>
                    <p className="text-sm text-slate-900 dark:text-white mt-0.5">
                      {student?.updated_at || "-"}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Editable profile fields */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border-l-4 border-primary-400">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
                  <Pencil className="w-4 h-4" />
                  Profile
                </h3>
                <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Display Name
                    </label>
                    <Input
                      label=""
                      placeholder="Enter your display name"
                      {...register("display_name")}
                      disabled={!isEditing}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        First Name
                      </label>
                      <Input
                        label=""
                        placeholder="Enter your first name"
                        {...register("first_name")}
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Last Name
                      </label>
                      <Input
                        label=""
                        placeholder="Enter your last name"
                        {...register("last_name")}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Email
                    </label>
                    <Input
                      label=""
                      value={student?.email || ""}
                      disabled
                      placeholder="name@gmail.com"
                      className="cursor-not-allowed"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Email cannot be changed
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Phone
                    </label>
                    <Input
                      label=""
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      maxLength={PHONE_MAX_CHARS}
                      placeholder="Enter your phone number"
                      {...register("phone", {
                        onChange: (e) => setValue("phone", sanitizePhone(e.target.value), { shouldValidate: true }),
                      })}
                      disabled={!isEditing}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Gender
                      </label>
                      <Select
                        label=""
                        options={[
                          { value: "", label: "Select..." },
                          { value: "male", label: "Male" },
                          { value: "female", label: "Female" },
                          { value: "other", label: "Other" },
                        ]}
                        {...register("gender")}
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Date of Birth
                      </label>
                      <Input
                        label=""
                        type="date"
                        placeholder="Select your date of birth"
                        {...register("dob")}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Address
                    </label>
                    <Input
                      label=""
                      placeholder="Enter your address"
                      {...register("address")}
                      disabled={!isEditing}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Guardian Name
                    </label>
                    <Input
                      label=""
                      placeholder="Enter your guardian name"
                      {...register("guardian_name")}
                      disabled={!isEditing}
                    />
                  </div>
                </form>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default StudentProfile;
